// What a Swedish invoice states about itself, and the arithmetic behind its due date —
// shared by the Deno backend and both apps' live previews, for the same reason the VAT
// table in ./se.ts is (FIN-602).
//
// This is not a style preference. Two of the three copies of `addDays` advanced the date
// in local time while the backend's advanced it in UTC, so every previewed due date was a
// day earlier than the issued PDF's at any positive UTC offset — Sweden included. Three
// copies of the same rule is what let that drift exist, exactly as three copies of the
// category-to-rate table did before this package was created.
//
// A rate and a sentence a customer can be held to are likewise not per-app strings: if a
// preview and a PDF word the late-payment terms differently, the sender proofed something
// other than what was sent.

import type { Language } from "./types.ts";

/**
 * The statutory inputs every dröjsmålsränta string on a Swedish invoice is assembled
 * from. Manually maintained; annual upkeep is tracked by FIN-674.
 *
 * These three numbers are the only place any of them appears: the prefilled rate, the
 * metadata line and the footer sentence are all derived, so changing one figure here
 * changes every rendered string at once.
 */
export interface StatutoryInterestProfile {
  /** Riksbanken's referensränta, in percent. */
  referenceRatePercent: number;
  /** Räntelagen's margin on top of the referensränta, in percentage points. */
  marginPoints: number;
  /** The calendar year the two figures above are stated as valid for. */
  statedForYear: number;
}

export const SE_STATUTORY_INTEREST: StatutoryInterestProfile = {
  referenceRatePercent: 2,
  marginPoints: 8,
  statedForYear: 2026,
};

/** Räntelagen's rate: referensränta + margin. Never written as a literal. */
export function statutoryInterestRatePercent(
  profile: StatutoryInterestProfile = SE_STATUTORY_INTEREST,
): number {
  return profile.referenceRatePercent + profile.marginPoints;
}

/**
 * The dröjsmålsränta Harmony prefills, and the ceiling a sender's override is capped at.
 */
export const DEFAULT_INTEREST_RATE_PERCENT = statutoryInterestRatePercent();

/**
 * Swedish decimal formatting, with no trailing zeros: 10 → "10", 8.5 → "8,5", 0 → "0".
 *
 * Trailing zeros are trimmed by walking the two fixed decimal places rather than by a
 * regex: every `0+$`-shaped pattern backtracks super-linearly, which sonarjs rightly
 * objects to for a value that reaches a rendered document.
 */
export function formatInterestRatePercent(rate: number): string {
  const [whole, decimals] = rate.toFixed(2).split(".");
  let end = decimals.length;
  while (end > 0 && decimals[end - 1] === "0") end--;
  return end === 0 ? whole : `${whole},${decimals.slice(0, end)}`;
}

/**
 * The rates below räntelagen's own that a sender may pick from.
 *
 * A fixed ladder rather than a typed number: the rate is a legal term on a customer-facing
 * document, and free text invited both typos and off-ladder rates nobody chose deliberately.
 *
 * 0 is on the ladder and is NOT the same as leaving the rate unset. A 0 renders
 * "Dröjsmålsränta: 0 %" — an explicit statement that no interest will be charged — while an
 * unset rate renders no line at all. Both are real choices a sender makes deliberately
 * (invoice-pdf-presentation.EC4/.AC6), so the selector has to offer each separately.
 *
 * Deliberately does NOT contain the statutory rate — `interestRateOptions` adds that from
 * the seller's own resolved rate, so the ladder stays correct if räntelagen's rate ever
 * moves off 10 %.
 */
export const INTEREST_RATE_CHOICES_BELOW_STATUTORY = [0, 2, 4, 6, 8] as const;

/**
 * The rates a sender's dröjsmålsränta selector offers, ascending.
 *
 * `statutoryRatePercent` is the seller's own resolved statutory rate (the backend's
 * `defaultInterestRatePercent`, routed by seller country), not a constant — so the one rate
 * that may be attributed to räntelagen is always present and always the ceiling. Hardcoding
 * `[2, 4, 6, 8, 10]` would quietly make that rate unreachable the year Riksbanken's
 * referensränta moves; the ladder below it simply truncates to the steps still lower.
 * Null while the seller profile is still loading, which yields no statutory option rather
 * than a guessed one.
 *
 * `currentRate` is included even when it is off the ladder or above the ceiling, so a rate
 * that arrived from somewhere else (an MCP-drafted invoice, an invoice copied from before
 * this selector existed) is shown as it stands rather than silently rewritten to a
 * neighbouring value. `interestRateProblem` is what reports it when it is out of range.
 */
export function interestRateOptions(
  statutoryRatePercent: number | null,
  currentRate: number | null = null,
): number[] {
  const rates = new Set<number>();
  if (statutoryRatePercent != null) {
    for (const rate of INTEREST_RATE_CHOICES_BELOW_STATUTORY) {
      if (rate < statutoryRatePercent) rates.add(rate);
    }
    rates.add(statutoryRatePercent);
  }
  if (currentRate != null) rates.add(currentRate);
  return [...rates].sort((a, b) => a - b);
}

/**
 * How one selectable rate reads in the dropdown.
 *
 * The statutory one is marked, because picking anything else silently drops the footer
 * sentence `buildStatutoryInterestNote` renders — the sender should see which choice carries
 * räntelagen's backing before making it, not discover it in the preview. 0 is marked for the
 * opposite reason: to keep it distinguishable from omitting the rate entirely.
 */
export function interestRateOptionLabel(
  rate: number,
  statutoryRatePercent: number | null,
): string {
  // This label is the sender's own rate-picker control, not customer-facing document text —
  // it stays Swedish-labelled regardless of documentLanguage, same as the rest of the consultant
  // form chrome around it.
  const stated = `${formatInterestRatePercent(rate)} %`;
  if (rate === statutoryRatePercent) return `${stated} (enligt räntelagen)`;
  // Spelled out because "0 %" sits one row from "Omit from invoice" and the two are
  // different documents: this one states that no interest is charged, that one says nothing.
  if (rate === 0) return `${stated} (no interest charged)`;
  return stated;
}

/**
 * Why a dröjsmålsränta a sender entered is not acceptable, or null when it is — shared so
 * both forms reject identically, and so the wording lives next to the rate it is measured
 * against. The answer is a reason, never a corrected value: a silently clamped rate puts a
 * number on a customer's invoice that the sender never approved (FIN-602 EC4b).
 */
export function interestRateProblem(rate: number | null, maxRate: number | null): string | null {
  if (rate == null || maxRate == null) return null;
  if (rate < 0) return "Cannot be negative";
  if (rate > maxRate) return `Cannot exceed ${maxRate}%`;
  return null;
}

/** Whether `rate` is räntelagen's own rate, and may therefore be stated as such. */
export function isStatutoryInterestRate(
  rate: number,
  profile: StatutoryInterestProfile = SE_STATUTORY_INTEREST,
): boolean {
  return rate === statutoryInterestRatePercent(profile);
}

/**
 * The dröjsmålsränta line in the invoice's metadata block, stating the rate that applies.
 *
 * `enligt räntelagen`/"per the Interest Act" is appended only while the statutory rate is
 * the one in use: a sender who lowered the rate is charging something räntelagen does not
 * give them, so attributing it to the statute would be a false claim on a customer-facing
 * document. A deliberate 0 % reads correctly through the same template as any other lowered
 * rate. `language` is the invoice document's own language (FIN-1580), not the viewer's app UI.
 */
export function buildInterestSummaryLine(
  rate: number,
  profile: StatutoryInterestProfile = SE_STATUTORY_INTEREST,
  language: Language = "sv",
): string {
  const label = language === "en" ? "Late-payment interest" : "Dröjsmålsränta";
  const stated = `${label}: ${formatInterestRatePercent(rate)} %`;
  if (!isStatutoryInterestRate(rate, profile)) return stated;
  return language === "en" ? `${stated} per the Swedish Interest Act (räntelagen)` : `${stated} enligt räntelagen`;
}

/**
 * The footer sentence spelling out where the statutory rate comes from, or `null` when
 * the sender lowered the rate off it and there is no statutory derivation to explain.
 *
 * Assembled from the profile's own figures rather than stored as a sentence, so a
 * referensränta change rewrites this and `buildInterestSummaryLine` together. `language` is
 * the invoice document's own language (FIN-1580), not the viewer's app UI.
 */
export function buildStatutoryInterestNote(
  rate: number,
  profile: StatutoryInterestProfile = SE_STATUTORY_INTEREST,
  language: Language = "sv",
): string | null {
  if (!isStatutoryInterestRate(rate, profile)) return null;
  const derived = formatInterestRatePercent(statutoryInterestRatePercent(profile));
  const margin = formatInterestRatePercent(profile.marginPoints);
  if (language === "en") {
    return `Late-payment interest per the Swedish Interest Act (räntelagen): the Riksbank's reference rate + ${margin} percentage points (currently ${derived} %, for ${profile.statedForYear}).`;
  }
  return `Dröjsmålsränta enligt räntelagen: Riksbankens referensränta + ${margin} procentenheter (f.n. ${derived} %, gäller för ${profile.statedForYear}).`;
}

/** What keeps the renderer's single-page A4 layout bounded. */
export const FREE_TEXT_MAX_LENGTH = 500;

/**
 * `iso` advanced by `days`, in UTC.
 *
 * UTC is load-bearing, not incidental: doing this in local time shifts the result a day
 * earlier at any positive UTC offset, which is what made the previewed due date disagree
 * with the issued PDF's Förfallodatum for every invoice.
 *
 * Returns "" for an unparseable date rather than throwing — the native date input a form
 * reads this from can be empty mid-edit, and a due-date preview must not crash the form
 * while the sender is between values.
 */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Today's date in Stockholm, as `YYYY-MM-DD` — the default an invoice is dated with. */
export function todayISO(): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Stockholm" }).format(new Date());
}
